import type {
  Conversation,
  ConversationNote,
  ConversationThread,
  FollowUp,
  MediaAsset,
  Message,
  MessengerAccount,
  MessengerNotification,
  MessengerRuntimeFeatures,
  MessengerRole,
  Recommendation,
} from '@crm/messenger';

export const E2E_NOW_ISO = '2026-02-10T15:00:00.000Z';
export const E2E_NOW_MS = Date.now();

export const E2E_IDENTITY_KEYS = [
  'agent',
  'cxs',
  'manager',
  'administrator',
] as const;

export type E2eIdentityKey = (typeof E2E_IDENTITY_KEYS)[number];

export const E2E_IDENTITIES: Record<E2eIdentityKey, MessengerAccount> = {
  agent: {
    name: 'Avery Agent',
    email: 'avery.agent@loandepot.com',
    tenantId: 'e2e-tenant',
    roles: ['agent'],
  },
  cxs: {
    name: 'Casey Cxs',
    email: 'casey.cxs@loandepot.com',
    tenantId: 'e2e-tenant',
    roles: ['cxs'],
  },
  manager: {
    name: 'Morgan Manager',
    email: 'morgan.manager@loandepot.com',
    tenantId: 'e2e-tenant',
    roles: ['manager'],
  },
  administrator: {
    name: 'Warren Dugan',
    email: 'warren.dugan@loandepot.com',
    tenantId: 'e2e-tenant',
    roles: ['administrator'],
  },
};

export type E2eMessengerState =
  | 'populated'
  | 'empty'
  | 'loading'
  | 'error'
  | 'denied'
  | 'offline'
  | 'thread-not-found'
  | 'send-fail'
  | 'send-validation-fail'
  | 'send-pending'
  | 'no-features'
  | 'notifications-error';

export const E2E_STATE_KEYS: readonly E2eMessengerState[] = [
  'populated',
  'empty',
  'loading',
  'error',
  'denied',
  'offline',
  'thread-not-found',
  'send-fail',
  'send-validation-fail',
  'send-pending',
  'no-features',
  'notifications-error',
];

function minutesAgo(minutes: number): string {
  return new Date(E2E_NOW_MS - minutes * 60000).toISOString();
}

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

function ago(minutes: number): string {
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  return `${Math.round(hours / 24)} d ago`;
}

function conversation(
  id: string,
  overrides: Partial<Conversation> = {},
): Conversation {
  return {
    id,
    customerName: `Customer ${id}`,
    initials: 'AB',
    channel: 'SMS',
    context: 'Application',
    preview: `Preview ${id}`,
    lastActivityAt: minutesAgo(30),
    timeAgo: ago(30),
    unreadCount: 0,
    pinned: false,
    flagged: false,
    archived: false,
    status: 'open',
    lastDeliveryFailed: false,
    customerPhone: '+1 555 0100',
    customerEmail: 'customer@example.com',
    leadRef: '',
    loanRef: '',
    nurtureStatus: '',
    ...overrides,
  };
}

function message(id: string, overrides: Partial<Message> = {}): Message {
  const sentAt = overrides.sentAt ?? minutesAgo(30);
  return {
    id,
    direction: 'inbound',
    senderPersona: 'customer',
    senderLabel: 'Customer',
    who: 'Customer',
    initials: 'CU',
    channel: 'SMS',
    sentAt,
    timeLabel: timeLabel(sentAt),
    status: 'Delivered',
    text: `Message ${id}`,
    attachments: [],
    acknowledged: true,
    failed: false,
    isLastRead: false,
    ...overrides,
  };
}

export const E2E_PRIMARY_CONVERSATION_ID = 'conv-patricia';

export function e2eConversations(): Conversation[] {
  return [
    conversation(E2E_PRIMARY_CONVERSATION_ID, {
      customerName: 'Patricia Garcia',
      initials: 'PG',
      context: 'Application LD-4821',
      preview: 'I can send the bank statements today.',
      lastActivityAt: minutesAgo(4),
      timeAgo: ago(4),
      unreadCount: 2,
      pinned: true,
      customerPhone: '+1 555 0148',
      customerEmail: 'patricia.garcia@example.com',
      leadRef: 'LEAD-1042',
      loanRef: 'LD-4821',
      nurtureStatus: 'active',
    }),
    conversation('conv-marcus', {
      customerName: 'Marcus Chen',
      initials: 'MC',
      context: 'Refinance inquiry',
      preview: 'Thanks, I will review the estimate tonight.',
      lastActivityAt: minutesAgo(38),
      timeAgo: ago(38),
      flagged: true,
      customerPhone: '+1 555 0119',
      customerEmail: 'marcus.chen@example.com',
      nurtureStatus: 'not-enrolled',
    }),
    conversation('conv-dana', {
      customerName: 'Dana Whitfield',
      initials: 'DW',
      context: 'Document collection',
      preview: 'Message could not be delivered.',
      lastActivityAt: minutesAgo(95),
      timeAgo: ago(95),
      lastDeliveryFailed: true,
      customerPhone: '+1 555 0177',
      nurtureStatus: 'inactive',
    }),
    conversation('conv-eliot', {
      customerName: 'Eliot Vargas',
      initials: 'EV',
      context: 'Pre-approval',
      preview: 'Sounds good, talk tomorrow.',
      lastActivityAt: minutesAgo(1500),
      timeAgo: ago(1500),
      archived: true,
      status: 'closed',
      nurtureStatus: 'completed',
    }),
    // Conversations the messages records list deep-links into.
    conversation('conv-james', {
      customerName: 'James Thompson',
      initials: 'JT',
      context: 'Purchase inquiry',
      preview: 'Follow-up sent — I will review the numbers tonight.',
      lastActivityAt: minutesAgo(18),
      timeAgo: ago(18),
      customerPhone: '+1 555 0142',
      customerEmail: 'james.thompson@example.com',
      nurtureStatus: 'active',
    }),
    conversation('conv-frank', {
      customerName: 'Frank Wilson',
      initials: 'FW',
      context: 'Application · Processing',
      preview: 'LO replied — conditions list is clear.',
      lastActivityAt: minutesAgo(60),
      timeAgo: ago(60),
      customerPhone: '+1 555 0188',
      customerEmail: 'frank.wilson@example.com',
      nurtureStatus: 'active',
    }),
  ];
}

export function e2eThread(id: string): ConversationThread | null {
  const base = e2eConversations().find((item) => item.id === id);
  if (!base) return null;
  if (id === E2E_PRIMARY_CONVERSATION_ID) {
    const messages = [
      message('m-1', {
        sentAt: minutesAgo(52),
        who: 'Patricia Garcia',
        initials: 'PG',
        text: 'Hi Patricia, following up on application LD-4821. We still need your October bank statements.',
      }),
      message('m-2', {
        sentAt: minutesAgo(48),
        direction: 'outbound',
        senderPersona: 'loan-officer',
        senderLabel: 'Loan officer',
        who: 'You',
        initials: 'Y',
        status: 'Read',
        text: 'Good morning! Uploading them now.',
        acknowledged: true,
      }),
      message('m-3', {
        sentAt: minutesAgo(10),
        who: 'Patricia Garcia',
        initials: 'PG',
        text: '[Message content unavailable]',
        attachments: [
          {
            name: 'bank-statement-preview.svg',
            contentType: 'image/svg+xml',
            url: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="320" height="180" viewBox="0 0 320 180"%3E%3Crect width="320" height="180" fill="%23f4f6f3"/%3E%3Crect x="28" y="24" width="264" height="132" rx="8" fill="white" stroke="%23cbe86b"/%3E%3Cpath d="M52 60h132M52 88h216M52 116h180" stroke="%231c140d" stroke-width="8" stroke-linecap="round"/%3E%3C/svg%3E',
          },
          { name: 'bank-statement.pdf', contentType: 'application/pdf' },
        ],
        acknowledged: false,
      }),
      message('m-4', {
        sentAt: minutesAgo(6),
        direction: 'outbound',
        senderPersona: 'nurture-assistant',
        senderLabel: 'Nurture assistant',
        who: 'You',
        initials: 'Y',
        status: 'failed',
        text: 'Reminder: your document checklist is ready.',
        acknowledged: false,
        failed: true,
        clientMessageId: 'cm-m4',
      }),
    ];
    let lastReadIndex = -1;
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const item = messages[index];
      if (item.acknowledged || ['read', 'delivered'].includes(item.status.toLowerCase())) {
        lastReadIndex = index;
        break;
      }
    }
    return {
      conversation: { ...base, unreadCount: 0 },
      messages: messages.map((item, index) => ({
        ...item,
        isLastRead: index === lastReadIndex,
      })),
    };
  }
  return {
    conversation: { ...base, unreadCount: 0 },
    messages: [
      message(`${id}-m-1`, {
        who: base.customerName,
        initials: base.initials,
        senderLabel: 'Customer',
        text: base.preview,
      }),
    ],
  };
}

export function e2eRuntimeFeatures(): MessengerRuntimeFeatures {
  return {
    suggestedReplies: true,
    mms: true,
    sendVCard: true,
    nurtureAgent: true,
    aiNoteGeneration: true,
  };
}

export function e2eRecommendations(): Recommendation[] {
  return [
    {
      id: 'rec-1',
      content: 'Great — October statements will complete the file.',
      kind: 'suggested-reply',
      suggestionBatchId: 'batch-1',
      suggestionBatchVersion: 1,
      suggestionId: 'sug-1',
      triggerMessageId: 'm-3',
    },
    {
      id: 'rec-2',
      content: 'Would you like me to send the secure upload link?',
      kind: 'suggested-reply',
      suggestionBatchId: 'batch-1',
      suggestionBatchVersion: 1,
      suggestionId: 'sug-2',
      triggerMessageId: 'm-3',
    },
  ];
}

export function e2eMedia(): MediaAsset[] {
  return [
    {
      id: 'media-1',
      name: 'Thumbs up',
      alt: 'Thumbs up animation',
      url: '/assets/mello-wordmark-dark.svg',
      thumbnailUrl: '/assets/mello-wordmark-dark.svg',
      category: 'reactions',
    },
    {
      id: 'media-2',
      name: 'Welcome',
      alt: 'Welcome animation',
      url: '/assets/mello-wordmark-dark.svg',
      thumbnailUrl: '/assets/mello-wordmark-dark.svg',
      category: 'greetings',
    },
  ];
}

export function e2eFollowUps(conversationId: string): FollowUp[] {
  if (conversationId !== E2E_PRIMARY_CONVERSATION_ID) return [];
  return [
    {
      id: 'fu-1',
      type: 'call',
      startsAt: minutesAgo(-1440),
      endsAt: minutesAgo(-1470),
      status: 'scheduled',
      details:
        'Follow up on the current loan request.\nPhone: +19492935851\nLoan Number: 114346828\nPOS: https://mello-sg1.loandepotdev.works/loan/by-number/114346828',
      channel: 'phone',
    },
    {
      id: 'fu-2',
      type: 'reminder',
      startsAt: minutesAgo(2880),
      endsAt: minutesAgo(2850),
      status: 'completed',
      details: 'Send document checklist reminder',
      channel: 'sms',
    },
  ];
}

export function e2eNotes(conversationId: string): ConversationNote[] {
  if (conversationId !== E2E_PRIMARY_CONVERSATION_ID) return [];
  return [
    {
      id: 'note-1',
      body: 'Borrower prefers SMS over email for document requests.',
      createdAt: minutesAgo(4320),
      archived: false,
      author: 'Will Dugan',
      mine: true,
    },
    {
      id: 'note-2',
      body: 'Old intake note.',
      createdAt: minutesAgo(10080),
      archived: true,
      author: 'Sarah Lee',
    },
    {
      id: 'note-3',
      body: 'AI summary for Sarah Lee: borrower asked about rate-lock timing and document checklist.',
      createdAt: minutesAgo(14000),
      archived: false,
      author: 'Sarah Lee',
      generated: true,
    },
  ];
}

export function e2eNotifications(): MessengerNotification[] {
  return [
    {
      id: 'ntf-1',
      type: 'message',
      priority: 'high',
      status: 'unread',
      title: 'New message from Patricia Garcia',
      message: 'I can send the bank statements today.',
      conversationId: E2E_PRIMARY_CONVERSATION_ID,
      actionUrl: `/communications/messages/${E2E_PRIMARY_CONVERSATION_ID}`,
      actionLabel: 'Open conversation',
      createdAt: minutesAgo(4),
      updatedAt: minutesAgo(4),
      readAt: '',
    },
    {
      id: 'ntf-2',
      type: 'follow-up',
      priority: 'normal',
      status: 'unread',
      title: 'Follow-up due tomorrow',
      message: 'Confirm bank statement upload with Patricia Garcia.',
      conversationId: E2E_PRIMARY_CONVERSATION_ID,
      actionUrl: `/communications/messages/${E2E_PRIMARY_CONVERSATION_ID}`,
      actionLabel: 'Open conversation',
      createdAt: minutesAgo(60),
      updatedAt: minutesAgo(60),
      readAt: '',
    },
  ];
}

export function e2eIdentityFor(role: MessengerRole): MessengerAccount {
  return E2E_IDENTITIES[role];
}
